import { useState } from 'react'
import { db } from '@/lib/db'
import { id } from '@instantdb/react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { toast } from 'sonner'
import { Building2, BarChart3, ShieldCheck, Trash2, Users, Info } from 'lucide-react'
import { ScrollArea } from '@/components/ui/scroll-area'

export default function AdminToolsPage() {
  const [orgName, setOrgName] = useState('我的测试企业')
  const [inviteCode, setInviteCode] = useState('TEST-888')
  const [isCreating, setIsCreating] = useState(false)
  const [isDeleting, setIsDeleting] = useState<string | null>(null)

  // 1. 查询基础数据
  const { data, isLoading } = db.useQuery({ 
    assets: {},
    organizations: {},
    projects: {},
    notes: {}
  })

  // 2. 创建企业逻辑
  const handleCreateOrg = async () => {
    if (!orgName || !inviteCode) return toast.error('请填写名称和邀请码')
    
    setIsCreating(true)
    const orgId = id()
    try {
      await db.transact(db.tx.organizations[orgId].update({
        id: orgId,
        name: orgName,
        invite_code: inviteCode.toUpperCase(),
        daily_limit: 300,
        is_initialized: false,
        created_at: Date.now(),
      }))
      toast.success(`企业创建成功！邀请码: ${inviteCode.toUpperCase()}`)
    } catch (error: any) {
      toast.error(`创建失败: ${error.message}`)
    } finally {
      setIsCreating(false)
    }
  }

  // 3. 深度删除企业及其关联数据
  const handleDeleteOrg = async (orgId: string, name: string) => {
    const confirmText = `确定要删除企业 "${name}" 吗？\n\n这将彻底删除该企业下的所有项目、素材、文案及配置，且不可恢复！\n\n请输入 "DELETE" 确认删除：`
    const userInput = prompt(confirmText)
    
    if (userInput !== 'DELETE') {
      return toast.info('已取消删除')
    }

    setIsDeleting(orgId)
    toast.loading(`正在深度清理企业 "${name}" 的数据...`)

    try {
      // 查询所有关联数据（通过 queryOnce 获取快照）
      const res = await db.queryOnce({
        projects: { $: { where: { org_id: orgId } } },
        assets: { $: { where: { org_id: orgId } } },
        note_styles: { $: { where: { org_id: orgId } } },
        copywriting_styles: { $: { where: { org_id: orgId } } },
        note_templates: { $: { where: { org_id: orgId } } },
        note_image_slots: { $: { where: { org_id: orgId } } },
        generation_logs: { $: { where: { org_id: orgId } } },
        profiles: { $: { where: { org_id: orgId } } },
        reference_images: { $: { where: { org_id: orgId } } },
      })

      const projects = res.data?.projects || []
      const initialAssets = res.data?.assets || []
      const noteStyles = res.data?.note_styles || []
      const copywritingStyles = res.data?.copywriting_styles || []
      const noteTemplates = res.data?.note_templates || []
      const noteImageSlots = res.data?.note_image_slots || []
      const generationLogs = res.data?.generation_logs || []
      const profiles = res.data?.profiles || []
      const referenceImages = res.data?.reference_images || []

      const projectIds = new Set(projects.map(p => p.id))

      // 查询项目下的笔记 (处理级联)
      const nRes = await db.queryOnce({ notes: {} })
      const allNotes = nRes.data?.notes || []
      const notesToDelete = allNotes.filter(n => projectIds.has(n.project_id))

      // 构建删除事务
      const txns: any[] = []
      
      projects.forEach(p => txns.push(db.tx.projects[p.id].delete()))
      notesToDelete.forEach(n => txns.push(db.tx.notes[n.id].delete()))
      initialAssets.forEach(a => txns.push(db.tx.assets[a.id].delete()))
      referenceImages.forEach(r => txns.push(db.tx.reference_images[r.id].delete()))
      noteStyles.forEach(s => txns.push(db.tx.note_styles[s.id].delete()))
      copywritingStyles.forEach(s => txns.push(db.tx.copywriting_styles[s.id].delete()))
      noteTemplates.forEach(t => txns.push(db.tx.note_templates[t.id].delete()))
      noteImageSlots.forEach(s => txns.push(db.tx.note_image_slots[s.id].delete()))
      generationLogs.forEach(l => txns.push(db.tx.generation_logs[l.id].delete()))
      profiles.forEach(p => txns.push(db.tx.profiles[p.id].update({ org_id: null })))
      
      // 最后删除企业
      txns.push(db.tx.organizations[orgId].delete())

      // 分批执行 (每批 40 个)
      const batchSize = 40
      for (let i = 0; i < txns.length; i += batchSize) {
        const batch = txns.slice(i, i + batchSize)
        await db.transact(batch)
      }

      toast.dismiss()
      toast.success(`企业 "${name}" 及其 ${txns.length - 1} 项关联数据已彻底清理`)
    } catch (error: any) {
      toast.dismiss()
      toast.error(`删除失败: ${error.message}`)
    } finally {
      setIsDeleting(null)
    }
  }

  const organizations = data?.organizations || []

  return (
    <div className="container max-w-5xl py-10 space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <ShieldCheck className="w-8 h-8 text-primary" /> 系统管理员工具
          </h1>
          <p className="text-muted-foreground mt-1">
            当前环境: <code className="bg-muted px-1 rounded text-xs text-primary font-mono">{import.meta.env.VITE_INSTANTDB_APP_ID}</code>
          </p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-3">
        {/* 数据统计 */}
        <Card className="md:col-span-1">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-green-500" /> 数据概览
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="p-3 bg-muted/50 rounded-lg border text-center">
                <div className="text-2xl font-bold">{isLoading ? '...' : data?.assets?.length || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase">素材 (Assets)</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg border text-center">
                <div className="text-2xl font-bold">{isLoading ? '...' : data?.organizations?.length || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase">企业 (Orgs)</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg border text-center">
                <div className="text-2xl font-bold">{isLoading ? '...' : data?.projects?.length || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase">项目 (Projects)</div>
              </div>
              <div className="p-3 bg-muted/50 rounded-lg border text-center">
                <div className="text-2xl font-bold">{isLoading ? '...' : data?.notes?.length || 0}</div>
                <div className="text-[10px] text-muted-foreground uppercase">笔记 (Notes)</div>
              </div>
            </div>
            <div className="flex items-start gap-2 p-3 bg-blue-50 text-blue-700 rounded-lg text-xs leading-relaxed">
              <Info className="w-4 h-4 mt-0.5 shrink-0" />
              <p>统计数据基于当前数据库 ID 的全量扫描。删除企业会自动清理上述所有关联记录。</p>
            </div>
          </CardContent>
        </Card>

        {/* 企业创建 */}
        <Card className="md:col-span-2 border-2 border-primary/10">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-500" /> 初始化新企业
            </CardTitle>
            <CardDescription>为新客户或测试环境快速生成入口</CardDescription>
          </CardHeader>
          <CardContent className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">企业名称</label>
              <Input 
                placeholder="例如：演示中心" 
                value={orgName} 
                onChange={(e) => setOrgName(e.target.value)} 
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">登录邀请码</label>
              <Input 
                placeholder="例如：VIP-666" 
                value={inviteCode} 
                onChange={(e) => setInviteCode(e.target.value.toUpperCase())} 
              />
            </div>
            <Button className="md:col-span-2 w-full mt-2" onClick={handleCreateOrg} disabled={isCreating}>
              {isCreating ? '执行中...' : '立即创建企业记录'}
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* 企业列表与管理 */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-orange-500" /> 企业列表管理
          </CardTitle>
          <CardDescription>
            管理当前数据库中的所有企业。注意：删除操作是不可逆的深度清理。
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ScrollArea className="h-[400px] pr-4">
            {isLoading ? (
              <div className="text-center py-10 text-muted-foreground">正在加载企业列表...</div>
            ) : organizations.length === 0 ? (
              <div className="text-center py-10 text-muted-foreground border-2 border-dashed rounded-xl">
                当前数据库为空，请先创建企业。
              </div>
            ) : (
              <div className="space-y-3">
                {organizations.sort((a, b) => (b.created_at || 0) - (a.created_at || 0)).map((org) => (
                  <div 
                    key={org.id} 
                    className="flex items-center justify-between p-4 bg-muted/30 rounded-xl border group hover:border-primary/30 transition-colors"
                  >
                    <div className="space-y-1">
                      <div className="font-semibold flex items-center gap-2">
                        {org.name}
                        <span className="text-[10px] bg-primary/10 text-primary px-2 py-0.5 rounded-full">
                          {org.invite_code}
                        </span>
                      </div>
                      <div className="text-[10px] text-muted-foreground font-mono">
                        ID: {org.id} • 创建于: {org.created_at ? new Date(org.created_at).toLocaleString() : '未知'}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => handleDeleteOrg(org.id, org.name)}
                        disabled={isDeleting === org.id}
                      >
                        {isDeleting === org.id ? (
                          "清理中..."
                        ) : (
                          <>
                            <Trash2 className="w-4 h-4 mr-1" /> 删除
                          </>
                        )}
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  )
}
